import {
  Calendar as ReactCalendar,
  CalendarProps,
  CalendarTileProperties,
} from "react-calendar";
import "react-calendar/dist/Calendar.css";
import styled from "styled-components";
import { ChangeEvent, useMemo, useState } from "react";
import { dateEquals } from "../../utils/dateUtils";

export type DateClickResult = { skipSelection: boolean } | void;

export interface ICalendar extends CalendarProps {
  onDateClick?: (date: Date) => DateClickResult;
  preSelectedDates?: Array<Date>;
  disableAll?: boolean;
  canAddMoreDates?: boolean;
  enabledDates?: Date[];
  disabledDates?: Date[];
  successDates?: Date[];
  warningDates?: Date[];
  dangerDates?: Date[];
  blueDates?: Date[];
  disableSelection?: boolean;
}

const Calendar = (props: ICalendar) => {
  const [selectedDates, setSelectedDates] = useState(
    props.preSelectedDates || new Array<Date>()
  );

  const tileClass = useMemo(() => {
    return tileClassFnFactory(selectedDates, props);
  }, [selectedDates, props]);

  const onDateChange = (value: Date, event: ChangeEvent<HTMLInputElement>) => {
    const resultado = props.onDateClick?.(value);

    if (props.disableSelection || resultado?.skipSelection) {
      return;
    }

    const wasSelected = selectedDates.some((date) => dateEquals(value, date));

    // If the date was already selected, un-select it when clicked again.
    if (wasSelected) {
      setSelectedDates((prevState) =>
        prevState.filter((date) => !dateEquals(value, date))
      );
    } else {
      setSelectedDates((prevState) => [...prevState, value]);
    }
  };

  const _tileIsDisabled = (tileProps: CalendarTileProperties) =>
    tileIsDisabled(tileProps, props, selectedDates);

  return (
    <StyledCalendar
      {...props}
      onChange={props.disableAll ? () => {} : onDateChange}
      tileDisabled={_tileIsDisabled}
      tileClassName={tileClass}
      locale={"es"}
    />
  );
};

const tileIsDisabled = (
  tileProps: CalendarTileProperties,
  props: ICalendar,
  selectedDates: Date[]
): boolean => {
  if (props.disableAll) {
    return true;
  }
  if (props.disabledDates) {
    const isDisabled = props.disabledDates.some((date: Date) =>
      dateEquals(tileProps.date, date)
    );
    if (isDisabled) {
      return true;
    }
  }
  if (
    props.canAddMoreDates === false &&
    props.canAddMoreDates !== undefined &&
    props.canAddMoreDates !== null
  ) {
    return !selectedDates.some((date) => dateEquals(tileProps.date, date));
  }
  if (!!props.enabledDates) {
    const disabled = !props.enabledDates.some((date) =>
      dateEquals(tileProps.date, date)
    );
    return disabled;
  }
  return false;
};

const StyledCalendar = styled(ReactCalendar)`
  .react-calendar {
    width: 400px;
    max-width: 100%;
    background-color: #fff;
    color: #222;
    border-radius: 8px;
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.2);
    font-family: ${(props) => props.theme.fontPrimary};
    line-height: 1.125em;
  }

  .react-calendar__tile {
    max-width: 100%;
    padding: 10px 10px;
    background: none;
    text-align: center;
    line-height: 16px;
  }

  .react-calendar__viewContainer {
    padding-left: 10px;
    padding-right: 10px;
    padding-bottom: 10px;
  }

  .react-calendar__navigation {
    padding-left: 10px;
    padding-right: 10px;
  }

  .react-calendar__month-view__days__day--weekend {
    color: revert;
  }

  .react-calendar__navigation button {
    color: ${(props) => props.theme.colors.main};
    min-width: 44px;
    background: none;
    font-size: 16px;
    margin-top: 8px;
  }

  .react-calendar__navigation button:enabled:hover,
  .react-calendar__navigation button:enabled:focus {
    background-color: #f8f8fa;
  }

  .react-calendar__navigation button[disabled] {
    background-color: #f0f0f0;
  }

  abbr[title] {
    text-decoration: none;
  }

  .react-calendar__tile:enabled:hover,
  .react-calendar__tile:enabled:focus {
    background: #f8f8fa;
    color: ${(props) => props.theme.colors.main};
  }

  .react-calendar__tile--now {
    background: ${(props) => props.theme.colors.mainShadow};
    font-weight: bold;
    color: ${(props) => props.theme.colors.main};
  }

  .react-calendar__tile--now:enabled:hover,
  .react-calendar__tile--now:enabled:focus {
    background: ${(props) => props.theme.colors.mainShadow};
    font-weight: bold;
    color: ${(props) => props.theme.colors.main};
  }

  .react-calendar__tile--hasActive:enabled:hover,
  .react-calendar__tile--hasActive:enabled:focus {
    background: #f8f8fa;
  }

  .react-calendar__tile--active {
    background: ${(props) => props.theme.colors.main};
    font-weight: bold;
    color: white;
  }

  .react-calendar__tile--active:enabled:hover,
  .react-calendar__tile--active:enabled:focus {
    background: ${(props) => props.theme.colors.main};
    color: white;
  }

  .react-calendar__tile--success {
    background: ${(props) => props.theme.colors.successShadow};
    color: ${(props) => props.theme.colors.success};
    font-weight: bold;
  }

  .react-calendar__tile--warning {
    background: ${(props) => props.theme.colors.warningShadow};
    color: ${(props) => props.theme.colors.warning};
    font-weight: bold;
  }

  .react-calendar__tile--danger {
    background: ${(props) => props.theme.colors.dangerShadow};
    color: ${(props) => props.theme.colors.error};
    font-weight: bold;
  }

  .react-calendar__tile--blue {
    background: ${(props) => props.theme.colors.blueShadow};
    color: ${(props) => props.theme.colors.blue};
    font-weight: bold;
  }

  .react-calendar--selectRange .react-calendar__tile--hover {
    background-color: #f8f8fa;
  }

  .react-calendar__tile--range {
    background: #f8f8fa;
    color: ${(props) => props.theme.colors.main};
  }

  .react-calendar__tile--rangeStart {
    background: ${(props) => props.theme.colors.main};
    color: white;
  }

  .react-calendar__tile--rangeEnd {
    background: ${(props) => props.theme.colors.main};
    color: white;
  }
`;

export default Calendar;

const tileClassFnFactory = (selectedDates: Date[], props: ICalendar) => {
  return (tileProps: CalendarTileProperties) => {
    const selected = selectedDates.some((date) =>
      dateEquals(tileProps.date, date)
    );
    if (selected) {
      return "react-calendar__tile--active";
    }

    const success = props.successDates?.some((date) =>
      dateEquals(tileProps.date, date)
    );

    if (success) {
      return "react-calendar__tile--success";
    }

    const warning = props.warningDates?.some((date) =>
      dateEquals(tileProps.date, date)
    );

    if (warning) {
      return "react-calendar__tile--warning";
    }

    const danger = props.dangerDates?.some((date) =>
      dateEquals(tileProps.date, date)
    );

    if (danger) {
      return "react-calendar__tile--danger";
    }

    const blue = props.blueDates?.some((date) =>
      dateEquals(tileProps.date, date)
    );

    if (blue) {
      return "react-calendar__tile--blue";
    }

    return null;
  };
};
